// src/App.js

import "./App.css";
import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
// ✅ Zap 아이콘 추가
import { Menu, X, Edit2, User, Zap } from "lucide-react";
import Footer from "./components/Footer/Footer";
import AnnouncementPopup from "./components/AnnouncementPopup/AnnouncementPopupServerError"; 
import AuthCallbackHandler from './components/Auth/AuthCallbackHandler';

// AuthContext로부터 세션을 가져오는 Login 컴포넌트
import Login from "./components/Auth/Login.jsx";
import SignInPage from "./components/Auth/SignInPage.js";
import RequireAuth from "./components/Auth/Auth.js";
import { useAuth } from "react-oidc-context"; 
import SetNicknamePage from "./components/Auth/setnicknamepage/SetNicknamePage.js";

import NavPage from "./components/pages/navigation/Navigation";
import MyPage from "./components/pages/mypage/MyPage";
import UserPage from "./components/pages/userpage/UserPage";
import PostsLists from "./components/pages/post/PostLists";
import PostDetail from "./components/pages/post/PostDetail.js";
import WritePostPage from "./components/pages/post/WritePost.js";

// ✅ MainContent 컴포넌트를 별도로 분리
import Allocation from "./components/pages/allocation/Allocation";
import BacktestPage from "./components/pages/backtest/BacktestPage";
import Risk from "./components/pages/risk/VaR";
import Simulation from "./components/pages/simulation/MonteCarlo.js";
import CurrencyDiagnosis from "./components/pages/currency/CurrencyDiagnosis";
import Correlation from "./components/pages/correlation/Correlation";
import Portfolios from "./components/pages/portfolios/Portfolios";
import PortfolioDetail from "./components/pages/portfolios/PortfolioDetail";
import SentimentAnalysis from "./components/pages/sentiment/SentimentAnalysis";

// react-i18next 훅 임포트
import { useTranslation } from "react-i18next";

// -----------------------------
// Router 감싸는 최상위 App
// -----------------------------
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
export default App;

// -----------------------------
// 실제 내용 담당 AppContent
// -----------------------------
function AppContent() {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();

  // 💡 공지사항 팝업 관련 상태 추가
  const [showAnnouncement, setShowAnnouncement] = useState(false);

  // 💡 공지사항 팝업 로직 추가
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const lastClosedDate = localStorage.getItem('announcementClosedDate');

    // 마지막으로 닫은 날짜가 오늘이 아니라면 팝업을 보여줌
    if (lastClosedDate !== today) {
      setShowAnnouncement(true);
    }
  }, []);

  // 💡 '오늘 하루 보지 않기' 핸들러
  const handleCloseAnnouncement = (dontShowToday) => {
    if (dontShowToday) {
      const today = new Date().toISOString().split('T')[0];
      localStorage.setItem('announcementClosedDate', today);
    }
    setShowAnnouncement(false);
  };

  // ✅ [수정됨] 로그인 후 인증 상태가 확인되면 닉네임 체크 (캐싱 적용)
  React.useEffect(() => {
    // 1. 로딩 중이면 대기
    if (auth.isLoading) {
      return;
    }

    // 체크해야 할 정적 경로 리스트
    const staticPaths = [
      "/", 
      // "/signin", 
      "/backtest", 
      "/allocation", 
      "/risk", 
      "/correlation", 
      "/simulation", 
      // "/currency-diagnosis", 
      // "/portfolios", 
      // "/posts"
    ];

    // 2. 인증은 되었으나, 지정된 경로들에 머물러 있는 경우 닉네임 체크 로직 실행
    if (auth.isAuthenticated && staticPaths.includes(location.pathname)) {
      
      const checkNicknameAndRedirect = async () => {
        // [최적화 1] 사용자 ID 기반 캐시 키 생성
        const userId = auth.user?.profile?.sub;
        const storageKey = `nickname_checked_${userId}`;

        // [최적화 2] 로컬 스토리지에 이미 "확인됨(true)"으로 되어 있다면 API 호출 없이 종료
        // (Auth.js나 SetNicknamePage에서 이미 저장했을 것이므로 믿고 넘어감)
        if (localStorage.getItem(storageKey) === 'true') {
            return; 
        }

        try {
          // 2-1. 닉네임 확인 API 호출 (캐시가 없을 때만 실행됨)
          const res = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/user/nickname/me`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${auth.user.id_token}`,
              },
            }
          );

          let hasNickname = false;
          if (res.status === 200) {
            const data = await res.json();
            if (data.nickname) {
              hasNickname = true;
              // [최적화 3] 닉네임이 확인되었으므로 스토리지에 저장 (다음번엔 호출 안 함)
              localStorage.setItem(storageKey, 'true');
            }
          }

          // 2-2. 원래 가려던 목적지 확인 (없으면 현재 경로 유지)
          const redirectPath = sessionStorage.getItem('redirect_after_login') || location.pathname;
          sessionStorage.removeItem('redirect_after_login');

          // 2-3. 분기 처리
          if (!hasNickname) {
            // 닉네임이 없으면 -> 닉네임 설정 페이지로 이동
            navigate("/set-nickname", { state: { from: redirectPath } });
          } else if (location.pathname === "/signin" || location.pathname === "/nav/auth/callback") {
            // 닉네임이 있고, 현재 위치가 로그인 처리 페이지라면 원래 목적지로 이동
            navigate(redirectPath, { replace: true });
          }

        } catch (error) {
          console.error("닉네임 확인 중 오류 발생:", error);
          // 에러 발생 시 안전하게 홈으로 이동
          navigate("/", { replace: true });
        }
      };

      checkNicknameAndRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, location.pathname, navigate, auth.user]);


  // 언어 전환 함수 
  const handleLanguageChange = (lng) => {
    // 현재 언어와 선택된 언어가 같으면 아무것도 하지 않고 함수를 종료
    if (i18n.language === lng) {
      return;
    }
    // 언어가 다를 때만 언어 변경
    i18n.changeLanguage(lng);
    // ✅ 로컬 스토리지에 변경된 언어 값 저장
    localStorage.setItem('i18nextLng', lng);
  };
  const isNavPage1 = location.pathname === "/";
  const isAllocPage = location.pathname === "/allocation";
  const isBacktestPage = location.pathname === "/backtest";
  const isWritePostPage = location.pathname === "/writepost";
  const isPostListsPage = location.pathname === "/posts" ; ;
  const isUserDetailPage = location.pathname.startsWith("/users/");
  const isPostDetailPage = location.pathname.startsWith("/posts/");
  const isPortfolioListsPage = location.pathname === "/portfolios";
  const isPortfolioDetailPage = location.pathname.startsWith("/portfolios/") && location.pathname !== "/portfolios";
  const isSimulationPage = location.pathname === "/simulation";
  const isSentimentPage = location.pathname === "/sentiment"; // 추가
  

  const mainContentClasses = `main-content-area ${
    isWritePostPage || 
    isPostListsPage || 
    isUserDetailPage ||
    isPostDetailPage ||
    isPortfolioListsPage 
    ? "no-top-padding" : ""}`;
  
  const maxWidthClass = 
  isPortfolioListsPage  || 
  isNavPage1  || 
  isPortfolioDetailPage  ||
  isBacktestPage ||
  isSentimentPage
  ? "max-width" : "";

  const finalContentClasses = `${mainContentClasses} ${maxWidthClass}`;

  // 모바일 메뉴 open/close 시 body 스크롤 방지
  React.useEffect(() => {
    if (menuOpen) {
      document.body.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
    }
    return () => {
      document.body.classList.remove("menu-open");
    };
  }, [menuOpen]);

  return (
    <div className={`App ${isSimulationPage ? 'simulation-page' : ''}`}>
      <header className="App-header">
        {/* 📌 왼쪽 영역: 로고 + 데스크탑 네비게이션 */}
        <div className="header-left">
          {/* QuanPort 로고 (Substack 스타일) */}
          <Link
            to="/"
            className="site-logo"
            onClick={() => setMenuOpen(false)}
          >
            QuanPort
          </Link>

          {/* 📌 데스크탑 네비게이션 (750px 이상에서 보임) */}
          <nav className="nav-bar">
            <ul>
              <li>
                <Link to="/posts">{t("Insights")}</Link>
              </li>
              <li>
                <Link to="/portfolios">{t("portfolioList")}</Link>
              </li>
              <li className="nav-dropdown">
                <span className="nav-category">
                  {t("portfolioOptimizationTools")}
                </span>
                <ul className="nav-subitems">
                  <li>
                    <Link to="/backtest">{t("navPage.quickActions.group2.backtest")}</Link>
                  </li>
                  <li>
                    <Link to="/allocation">
                      {t("navPage.quickActions.group2.alloc")}
                    </Link>
                  </li>
                  {/* ✅ [데스크탑] 시장 심리 메뉴 추가 */}
                  <li>
                    <Link to="/sentiment">{t("navPage.quickActions.group2.sentiment")}</Link>
                  </li>
                  <li>
                    <Link to="/risk">{t("navPage.quickActions.group2.risk")}</Link>
                  </li>
                  <li>
                    <Link to="/correlation">{t("navPage.quickActions.group2.corr")}</Link>
                  </li>
               
                  <li>
                    <Link to="/simulation">{t("navPage.quickActions.group2.sim")}</Link>
                  </li>
                </ul>
              </li>
            </ul>
          </nav>
        </div>

        {/* 📌 오른쪽 영역: 글쓰기 버튼, 로그인/인사말, 언어 전환, 모바일 햄버거 버튼 */}
        <div className="header-right-actions">
          {/* 글쓰기 버튼 (Medium 스타일) */}
          <Link
            to="/writepost"
            className="write-post-button"
            onClick={() => setMenuOpen(false)}
          >
            <Edit2 size={18} />
            <span className="write-text"> {t("writePost.button")}</span>
          </Link>

          {/* My Page 링크를 아이콘으로 대체 */}
          <Link
            to="/mypage"
            className="my-page-icon-button"
            onClick={() => setMenuOpen(false)}
          >
            <User size={24} /> {/* User 아이콘 사용, 크기 조정 */}
          </Link>

          {/* 로그인/인사말 영역 */}
          <Login />

          {/* 언어 전환 UI */}
          <div className="language-switcher">
            <button
              onClick={() => handleLanguageChange("ko")}
              className={i18n.language === "ko" ? "active" : ""}
            >
              KR
            </button>
            <button
              onClick={() => handleLanguageChange("en")}
              className={i18n.language === "en" ? "active" : ""}
            >
              EN
            </button>
          </div>

          {/* 📌 모바일 햄버거 버튼 */}
          <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      {/* 📌 메인 라우팅 설정을 감싸는 div 추가 (헤더 겹침 방지) */}
      <main className={finalContentClasses}>
        <Routes>
          <Route path="/" element={<NavPage />} />
          <Route path="/nav" element={<NavPage to="/" replace />} />
          <Route path="/set-nickname" element={<SetNicknamePage />} />
          
          <Route path="/nav/auth/callback" element={<AuthCallbackHandler />}/>
          <Route path="/silent-renew" element={<div />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route
            path="/mypage"
            element={
              <RequireAuth>
                <MyPage />
              </RequireAuth>
            }
          />

          <Route
            path="/portfolios/new"
            element={
              <RequireAuth>
                <Portfolios /> 
              </RequireAuth>
            }
          />
          <Route
            path="/writepost"
            element={
              <RequireAuth>
                <WritePostPage />
              </RequireAuth>
            }
          />
          <Route
            path="/posts/:postId/edit"
            element={
              <RequireAuth>
                <WritePostPage />
              </RequireAuth>
            }
          />
          <Route
            path="/allocation/new-portfolio"
            element={
              <RequireAuth>
                <Allocation />
              </RequireAuth>
            }
          />
          <Route path="/backtest" element={<BacktestPage />} />
          <Route path="/allocation" element={<Allocation />} />
          <Route path="/risk" element={<Risk />} />
          <Route path="/correlation" element={<Correlation />} />
          <Route path="/simulation" element={<Simulation />} />
          <Route path="/currency-diagnosis" element={<CurrencyDiagnosis />} />
          <Route path="/portfolios" element={<Portfolios />} />
          <Route path="/portfolios/:id" element={<PortfolioDetail />} />
          <Route path="/users/:userId/profile" element={<UserPage />} />
          <Route path="/posts" element={<PostsLists />} />
          <Route path="/posts/:postId" element={<PostDetail />} />
          <Route path="/sentiment" element={<SentimentAnalysis />} />
           
        </Routes>
      </main>

      {/* 📌 모바일 메뉴와 오버레이는 최상위 레벨에서 관리 */}
      <div className={`mobile-menu ${menuOpen ? "open" : ""}`}>
          <button className="close-btn" onClick={() => setMenuOpen(false)}>
            <X size={24} />
          </button>
          <ul className="mobile-menu-list">
            <li>
              <Link to="/" onClick={() => setMenuOpen(false)}>
                QuanPort
              </Link>
            </li>
            <li>
              <Link to="/portfolios" onClick={() => setMenuOpen(false)}>
                {t("navPage.quickActions.ranking2")}
              </Link>
            </li>
            <li>
              <Link to="/posts" onClick={() => setMenuOpen(false)}>
                {t("navPage.quickActions.insight2")}
              </Link>
            </li>
            {/* My Page 링크 추가 (모바일) - 모바일에서는 텍스트 유지하거나 아이콘+텍스트 */}
            <li>
              <Link to="/mypage" onClick={() => setMenuOpen(false)}>
                <User size={18} style={{ marginRight: "10px" }} />{" "}
                {t("myPage")}{" "}
                {/* 아이콘과 텍스트 함께 */}
              </Link>
            </li>
            <li>
              <span className="mobile-menu-category">
                {t("portfolioOptimizationTools")}
              </span>
              <ul className="mobile-menu-subitems">
                <li>
                  <Link to="/backtest" onClick={() => setMenuOpen(false)}>
                    {t("navPage.quickActions.group2.backtest")}
                  </Link>
                </li>
                <li>
                  <Link to="/allocation" onClick={() => setMenuOpen(false)}>
                    {t("navPage.quickActions.group2.alloc")}
                  </Link>
                </li>
                {/* ✅ [모바일] 시장 심리 메뉴 추가 */}
                <li>
                  <Link to="/sentiment" onClick={() => setMenuOpen(false)}>
                    {t("navPage.quickActions.group2.sentiment")}
                  </Link>
                </li>
                <li>
                  <Link to="/risk" onClick={() => setMenuOpen(false)}>
                    {t("navPage.quickActions.group2.risk")}
                  </Link>
                </li>
                <li>
                  <Link to="/correlation" onClick={() => setMenuOpen(false)}>
                    {t("navPage.quickActions.group2.corr")}
                  </Link>
                </li>
                <li>
                  <Link to="/simulation" onClick={() => setMenuOpen(false)}>
                    {t("navPage.quickActions.group2.sim")}
                  </Link>
                </li>
           
              </ul>
            </li>
            {/* 커뮤니티 또는 다른 주요 메뉴 추가 예정 */}
            <li>
              <Link to="/writepost" onClick={() => setMenuOpen(false)}>
                <Edit2 size={18} /> {t("writePost.button")}
              </Link>
            </li>
            <li className="mobile-language-switcher-item"> 
              <div className="mobile-language-switcher">
                <button
                  onClick={() => { handleLanguageChange("ko"); setMenuOpen(false); }}
                  className={i18n.language === "ko" ? "active" : ""}
                >
                  KR
                </button>
                <button
                  onClick={() => { handleLanguageChange("en"); setMenuOpen(false); }}
                  className={i18n.language === "en" ? "active" : ""}
                >
                  EN
                </button>
              </div>
            </li>
            <li>
              {/* 모바일 메뉴 내부에 Login 컴포넌트 위치 조정 가능 */}
              <Login isMobile={true} onClick={() => setMenuOpen(false)} />
            </li>
          </ul>
      </div>

      {/* ✅ 여기에 공지사항 팝업을 추가해야 화면에 나옵니다 */}
      {/* {showAnnouncement && (
        <AnnouncementPopup onClose={handleCloseAnnouncement} />
      )} */}

      {/* 📌 오버레이: 메뉴가 열려있을 때만 표시 */}
      {menuOpen && (
        <div
          className="App-content-overlay"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <Footer />
    </div>
  );
}